import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API, { setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (getToken()) {
      checkAuth();
    } else {
      setUser(null);
    }
  }, [checkAuth]);

  const login = async (email, password, remember = true) => {
    const { data } = await API.post("/auth/login", { email, password, remember });
    if (data.access_token) setToken(data.access_token, remember);
    setUser(data);
    return data;
  };

  const register = async (email, password, firstName = "") => {
    const { data } = await API.post("/auth/register", {
      email,
      password,
      first_name: firstName,
    });
    // Fresh signups default to persistent session.
    if (data.access_token) setToken(data.access_token, true);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch {
      // ignore
    }
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
